import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '../layouts';
import { Dashboard } from '../pages/Dashboard';
import { Games, GameHome } from '../pages/Games';
import { Seasons, Home } from '../pages/Seasons';
import { Clients, Users, Players, Opponents, StatTakers } from '../pages/Setup';
import PlayerStatSheetPage from '../pages/Seasons/PlayerStatSheetPage';
import { Profile } from '../pages/Profile';
import { Account } from '../pages/Account';
import { HelpCenter } from '../pages/HelpCenter';
import { CoachCorner } from '../pages/CoachCorner';
import { Billing } from '../pages/Billing';
import { Leaderboards } from '../pages/Leaderboards';
import ProtectedRoute, { getFirstAccessibleRoute } from './ProtectedRoute';

interface PrivateRoutesProps {
  onLogout: () => void;
}

const PrivateRoutes: React.FC<PrivateRoutesProps> = ({ onLogout }) => {
  const getDefaultRedirect = () => {
    // Use the same logic as ProtectedRoute to find first accessible route
    return getFirstAccessibleRoute();
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={onLogout} />
          </ProtectedRoute>
        }
      >
        {/* Default redirect */}
        <Route index element={<Navigate to={getDefaultRedirect()} replace />} />

        {/* Admin Dashboard - Employees only */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute requireEmployee>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Season and Game Routes */}
        <Route
          path="seasons"
          element={
            <ProtectedRoute requirePermission="seasons">
              <Seasons />
            </ProtectedRoute>
          }
        />
        <Route path="seasons/:seasonId/*" element={<Home />} />
        <Route path="games" element={<Games />} />
        <Route path="games/:gameId/*" element={<GameHome />} />

        {/* Setup Routes - Clients page is employees only */}
        <Route
          path="clients"
          element={
            <ProtectedRoute requireEmployee>
              <Clients />
            </ProtectedRoute>
          }
        />
        <Route
          path="leaderboards"
          element={
            <ProtectedRoute requireEmployee>
              <Leaderboards />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute requirePermission="users">
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="players"
          element={
            <ProtectedRoute requirePermission="players">
              <Players />
            </ProtectedRoute>
          }
        />
        <Route
          path="players/:playerId/stats"
          element={
            <ProtectedRoute requirePermission="players">
              <PlayerStatSheetPage variant="career" />
            </ProtectedRoute>
          }
        />
        <Route
          path="opponents"
          element={
            <ProtectedRoute requirePermission="opponents">
              <Opponents />
            </ProtectedRoute>
          }
        />

        {/* Setup: Stat Takers - Available to coaches */}
        <Route
          path="setup/stat-takers"
          element={
            <ProtectedRoute>
              <StatTakers />
            </ProtectedRoute>
          }
        />

        {/* Profile - Available to all authenticated users */}
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* Account - Available to client owners (head coaches and admins) */}
        <Route
          path="account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />

        {/* Help Center - Available to all authenticated users */}
        <Route
          path="help-center"
          element={
            <ProtectedRoute>
              <HelpCenter />
            </ProtectedRoute>
          }
        />

        {/* Coach Corner - Available to all authenticated users */}
        <Route
          path="coach-corner"
          element={
            <ProtectedRoute>
              <CoachCorner />
            </ProtectedRoute>
          }
        />

        {/* Billing - Available to Head Coach and Assistant Coach only */}
        <Route
          path="billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all - redirect to appropriate default */}
      <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
    </Routes>
  );
};

export default PrivateRoutes;
